import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Heart, Home, MapPin, BedDouble, Bath, Car, TrendingUp,
  Truck, Plane, CalendarClock, Save, ChevronRight, Route, DollarSign, CheckCircle2,
  FileDown, Briefcase,
} from "lucide-react";
import { relocationApi } from "@/api/relocation";
import LocalDiscovery from "@/components/local/LocalDiscovery";
import RelocationBoard from "@/components/dashboard/RelocationBoard";
import { downloadVaultAsset } from "@/api/compliance";
import CreditPaywall from "@/components/billing/CreditPaywall";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const ORIGINS = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Hobart", "Canberra"];

/* Phase 30 · Slice A — Native Housing & Itinerary Desk. 10-listing AU grid →
   favourite → auto-generated relocation path (cost + itinerary + timeline) →
   save a durable travel_manifest to the Vault. */
export default function RelocationDesk({ onBack }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState("Sydney");
  const [plan, setPlan] = useState(null);       // relocate response
  const [planning, setPlanning] = useState(null); // listing_id being planned
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("sub") === "board" ? "board" : "housing"); // housing | local | board

  const load = async () => {
    try { const d = await relocationApi.listings(); setListings(d.listings || []); }
    catch { toast.error("Could not load listings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleFav = async (id) => {
    setListings((ls) => ls.map((l) => l.id === id ? { ...l, favorited: !l.favorited } : l));
    try { await relocationApi.favorite(id); } catch { load(); }
  };

  const buildPath = async (id) => {
    setPlanning(id); setPlan(null); setSavedId(null);
    try {
      const d = await relocationApi.relocate(id, origin);
      setPlan(d);
    } catch { toast.error("Could not build relocation path"); }
    finally { setPlanning(null); }
  };

  const saveManifest = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const r = await relocationApi.saveManifest({
        listing_id: plan.listing.id,
        relocation: { moving_cost: plan.moving_cost, timeline: plan.timeline, grand_total: plan.grand_total },
        itinerary: plan.itinerary,
      });
      setSavedId(r.asset_id);
      toast.success("Travel manifest saved to Vault");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-cream" data-testid="relocation-desk">
      <header className="shrink-0 flex items-center gap-3 border-b-2 border-charcoal/10 px-4 pt-4 pb-3">
        <button onClick={plan ? () => setPlan(null) : onBack} data-testid="relocation-back" className="grid h-10 w-10 place-items-center rounded-full border-2 border-charcoal bg-white shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70">Trip & Travel Planner</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-1.5"><Home size={18} /> {plan ? "Trip Path" : tab === "local" ? "Local: What's Going On" : tab === "board" ? "Relocation Board" : "Housing Desk"}</h1>
        </div>
      </header>

      {!plan && (
        <div className="shrink-0 flex gap-2 px-4 py-2 border-b-2 border-charcoal/10" data-testid="travel-tabs">
          {[["housing", "Housing"], ["local", "Local"], ["board", "Board"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`travel-tab-${k}`}
              className={`flex-1 rounded-xl border-2 border-charcoal px-3 py-2 font-mono text-[10px] font-black uppercase tracking-widest transition-[transform,box-shadow] ${tab === k ? "bg-gold text-black shadow-[3px_3px_0px_#1A1A1A]" : "bg-white text-charcoal/70 shadow-none"}`}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      {tab === "board" && !plan ? (
        <RelocationBoard />
      ) : tab === "local" && !plan ? (
        <LocalDiscovery />
      ) : loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="animate-spin text-charcoal/70" /></div>
      ) : plan ? (
        <RelocationPath plan={plan} saving={saving} savedId={savedId} onSave={saveManifest} />
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-3 pb-28 space-y-3">
          <div className="flex items-center gap-2" data-testid="relocation-origin">
            <span className="font-mono text-[9px] uppercase tracking-widest text-charcoal/70">Moving from</span>
            <select value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="relocation-origin-select" className="flex-1 bg-white border-2 border-charcoal rounded-xl px-2 py-1.5 font-mono text-[11px] font-bold">
              {ORIGINS.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          {listings.map((l) => (
            <div key={l.id} data-testid={`listing-${l.id}`} className="overflow-hidden rounded-2xl border-2 border-charcoal bg-white shadow-[3px_3px_0px_#1A1A1A]">
              <div className="relative h-32 w-full">
                <img src={l.image} alt={l.suburb} className="h-full w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-full bg-charcoal/85 px-2 py-0.5 font-mono text-[8px] font-black uppercase tracking-widest text-gold">{l.portal}</span>
                <button onClick={() => toggleFav(l.id)} data-testid={`fav-${l.id}`} className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full border-2 border-charcoal bg-white active:scale-90">
                  <Heart size={15} className={l.favorited ? "fill-rust text-rust" : "text-charcoal/70"} />
                </button>
                <span className="absolute bottom-2 right-2 rounded-lg bg-white/95 px-2 py-1 font-serif text-lg font-bold text-charcoal">{money(l.price)}</span>
              </div>
              <div className="p-3">
                <p className="font-sans text-[15px] font-bold leading-tight text-charcoal">{l.title}</p>
                <p className="flex items-center gap-1 font-mono text-[10px] text-charcoal/70"><MapPin size={11} /> {l.suburb}, {l.city} {l.state}</p>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-charcoal/70">
                  <span className="flex items-center gap-1"><BedDouble size={12} /> {l.beds}</span>
                  <span className="flex items-center gap-1"><Bath size={12} /> {l.baths}</span>
                  <span className="flex items-center gap-1"><Car size={12} /> {l.cars}</span>
                  <span className="flex items-center gap-1 text-emerald-600"><TrendingUp size={12} /> {l.growth_1y_pct}% 1yr</span>
                  <span className="text-sky-600">{l.yield_pct}% yield</span>
                  <span className="text-charcoal/70">Walk {l.walk_score}</span>
                </div>
                <button onClick={() => buildPath(l.id)} disabled={planning === l.id} data-testid={`relocate-${l.id}`}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border-2 border-charcoal bg-gold py-2.5 font-mono text-[9px] font-black uppercase tracking-widest text-black shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
                  {planning === l.id ? <Loader2 size={13} className="animate-spin" /> : <Route size={13} />} Plan Relocation <ChevronRight size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RelocationPath({ plan, saving, savedId, onSave }) {
  const mc = plan.moving_cost, it = plan.itinerary;
  const [businessMode, setBusinessMode] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [paywall, setPaywall] = useState(null);

  const renderPack = async () => {
    setRendering(true);
    try {
      const r = await relocationApi.renderPack({
        listing_id: plan.listing.id,
        origin_city: plan.origin_city,
        moving_cost: mc,
        itinerary: it,
        timeline: plan.timeline,
        grand_total: plan.grand_total,
        business_relocation: businessMode,
        manifest_asset_id: savedId || undefined,
      });
      await downloadVaultAsset(r.asset_id, r.filename);
      toast.success(
        (r.charged ? `Relocation pack · ${r.cost} Credits` : "Relocation pack ready")
        + (r.audit_logged ? " · logged to tax ledger" : "")
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail) setPaywall(detail);
      else toast.error(typeof detail === "string" ? detail : "Render failed");
    } finally { setRendering(false); }
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-3 pb-28 space-y-3" data-testid="relocation-path">
      <div className="rounded-2xl border-2 border-charcoal bg-charcoal p-3 text-cream">
        <p className="font-mono text-[9px] uppercase tracking-widest text-gold">{plan.origin_city} → {plan.destination_city}</p>
        <p className="font-serif text-xl">{plan.listing.suburb}, {plan.listing.state}</p>
        <p className="mt-1 font-mono text-[11px] text-cream/70">All-in relocation estimate <span className="font-black text-gold">{money(plan.grand_total)}</span></p>
      </div>

      {/* Moving cost card */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="moving-cost-card">
        <p className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest text-charcoal"><Truck size={13} /> Moving Cost Estimate</p>
        <div className="mt-2 space-y-1">
          {[["Removalist", mc.removalist], ["Transit insurance", mc.transit_insurance], ["Packing materials", mc.packing_materials], ["End-of-lease clean", mc.end_of_lease_clean], ["Utility connections", mc.utility_connections]].map(([k, v]) => (
            <div key={k} className="flex justify-between font-mono text-[11px] text-charcoal/70"><span>{k}</span><span>{money(v)}</span></div>
          ))}
          <div className="mt-1 flex justify-between border-t-2 border-charcoal/10 pt-1 font-mono text-[12px] font-black text-charcoal"><span>Total ({mc.distance_km} km)</span><span>{money(mc.total)}</span></div>
        </div>
      </div>

      {/* Itinerary builder */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="itinerary-card">
        <p className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest text-charcoal"><Plane size={13} /> Transit Itinerary · {it.recommended}</p>
        <div className="mt-2 space-y-2">
          {it.legs.map((leg, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border-2 border-charcoal/10 bg-cream p-2">
              <span className="rounded-lg bg-charcoal px-2 py-1 font-mono text-[8px] font-black uppercase text-gold">{leg.mode}</span>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] font-bold text-charcoal truncate">{leg.from} → {leg.to}</p>
                <p className="font-mono text-[9px] text-charcoal/70">{leg.duration_h}h · {leg.note}</p>
              </div>
              <span className="font-mono text-[11px] font-black text-charcoal">{money(leg.est_cost)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Day-by-day timeline */}
      <div className="rounded-2xl border-2 border-charcoal bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="timeline-card">
        <p className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest text-charcoal"><CalendarClock size={13} /> Day-by-Day Moving Timeline</p>
        <div className="mt-2 space-y-1.5">
          {plan.timeline.map((t, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`shrink-0 w-11 text-center rounded-md px-1 py-0.5 font-mono text-[8px] font-black ${t.day_offset === 0 ? "bg-gold text-black" : "bg-charcoal/10 text-charcoal/70"}`}>{t.label}</span>
              <p className="font-mono text-[10px] leading-snug text-charcoal/80">{t.task}</p>
            </div>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 rounded-2xl border-2 border-charcoal/15 bg-white p-3" data-testid="business-relocation-toggle">
        <input type="checkbox" checked={businessMode} onChange={(e) => setBusinessMode(e.target.checked)} data-testid="business-relocation-checkbox" className="h-4 w-4 accent-charcoal" />
        <Briefcase size={14} className="text-charcoal/60" />
        <span className="font-mono text-[10px] leading-tight text-charcoal/70">Business / commercial relocation — log as a tax-deductible expense</span>
      </label>

      <button onClick={renderPack} disabled={rendering} data-testid="render-pack" className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-charcoal bg-rust py-3 font-mono text-[11px] font-black uppercase tracking-widest text-white shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
        {rendering ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Render Relocation Pack · 40 Cr
      </button>

      <button onClick={onSave} disabled={saving || !!savedId} data-testid="save-manifest" className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-charcoal bg-gold py-3 font-mono text-[11px] font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : savedId ? <CheckCircle2 size={15} /> : <Save size={15} />}
        {savedId ? "Saved to Vault" : "Save Travel Manifest"}
      </button>
      <CreditPaywall detail={paywall} onClose={() => setPaywall(null)} />
    </div>
  );
}
